"use client";

/**
 * Aggregate-coverage hook — answers "what's covered for this user across
 * every household they're a member of?"
 *
 * Used by:
 *   - The pre-paywall coverage gate (Decision #2): suppress the paywall
 *     when every one of the user's households is already covered by another
 *     adult.
 *   - The "Covered by [Payer Name]" banner (Decision #7): show non-payer
 *     adults who is covering them and until when.
 *   - The cancellation pre-confirmation modal: list every household that
 *     would lose coverage if the user cancels their own sub.
 *
 * Reads `member_profiles/{uid}.households`, then snapshots each household's
 * `subscription` block. All listeners are torn down on unmount.
 */

import { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuthContext } from "@/contexts/AuthContext";
import type { HouseholdSubscription } from "@/lib/models";

/** Subscription state with the household it's bound to. */
export interface CoverageEntry {
  householdId: string;
  subscription: HouseholdSubscription | undefined;
  /** True if `subscription.status` is one of {active, trialing, past_due}. */
  isCovered: boolean;
}

export interface CallerCoverageState {
  loading: boolean;
  /** Every household the user is a member of, with its current sub state. */
  entries: CoverageEntry[];
  /** True when at least one household exists and **all** are covered. */
  allHouseholdsCovered: boolean;
  /**
   * When the user is in covered households *and* they are themselves the
   * payer for at least one of those subs. Drives Customer Portal access UI.
   */
  isPayerForAny: boolean;
  /**
   * The subscription covering the user's current/active household, if any.
   * Convenience for "Covered by [Name]" banner that wraps this hook.
   */
  coveringHouseholds(payerUid: string): CoverageEntry[];
}

const COVERED_STATUSES = new Set(["active", "trialing", "past_due"]);

function isCoveredStatus(sub: HouseholdSubscription | undefined): boolean {
  if (!sub) return false;
  if (!sub.tier) return false;
  return Boolean(sub.status && COVERED_STATUSES.has(sub.status));
}

export function useCallerCoverage(): CallerCoverageState {
  const { user } = useAuthContext();
  const uid = user?.uid;

  const [householdIds, setHouseholdIds] = useState<string[] | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [hhSubs, setHhSubs] = useState<Record<string, HouseholdSubscription | undefined>>({});
  const [hhLoaded, setHhLoaded] = useState<Record<string, boolean>>({});

  // Listen on the caller's profile to discover which households they belong to.
  useEffect(() => {
    if (!uid) {
      setHouseholdIds(null);
      setProfileLoaded(true);
      return;
    }
    const ref = doc(db, "member_profiles", uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data() as { households?: string[] } | undefined;
        const list = data?.households ?? [];
        setHouseholdIds(list);
        setProfileLoaded(true);
      },
      () => {
        setHouseholdIds([]);
        setProfileLoaded(true);
      },
    );
    return unsub;
  }, [uid]);

  // For each household the user is in, listen on its subscription field.
  useEffect(() => {
    if (!householdIds) return;
    if (householdIds.length === 0) {
      setHhSubs({});
      setHhLoaded({});
      return;
    }
    setHhLoaded((prev) => {
      const next: Record<string, boolean> = {};
      for (const id of householdIds) next[id] = prev[id] ?? false;
      return next;
    });

    const unsubs = householdIds.map((hhId) => {
      const ref = doc(db, "households", hhId);
      return onSnapshot(
        ref,
        (snap) => {
          const data = snap.data() as { subscription?: HouseholdSubscription } | undefined;
          setHhSubs((prev) => ({ ...prev, [hhId]: data?.subscription }));
          setHhLoaded((prev) => ({ ...prev, [hhId]: true }));
        },
        () => {
          setHhSubs((prev) => ({ ...prev, [hhId]: undefined }));
          setHhLoaded((prev) => ({ ...prev, [hhId]: true }));
        },
      );
    });
    return () => unsubs.forEach((u) => u());
    // We intentionally re-key on the array contents — JSON.stringify keeps the
    // effect stable when the list is the same set in a different order.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(householdIds ?? [])]);

  return useMemo(() => {
    const ids = householdIds ?? [];
    const allHhLoaded = ids.length === 0
        ? profileLoaded
        : profileLoaded && ids.every((id) => hhLoaded[id]);

    const entries: CoverageEntry[] = ids.map((id) => {
      const subscription = hhSubs[id];
      return {
        householdId: id,
        subscription,
        isCovered: isCoveredStatus(subscription),
      };
    });

    const allHouseholdsCovered =
      entries.length > 0 && entries.every((e) => e.isCovered);
    const isPayerForAny =
      uid != null
      && entries.some((e) => e.isCovered && e.subscription?.payer_uid === uid);

    return {
      loading: !allHhLoaded,
      entries,
      allHouseholdsCovered,
      isPayerForAny,
      coveringHouseholds: (payerUid: string) =>
        entries.filter(
          (e) => e.isCovered && e.subscription?.payer_uid === payerUid,
        ),
    };
  }, [householdIds, hhSubs, hhLoaded, profileLoaded, uid]);
}
